import { loadStripe, type Stripe } from '@stripe/stripe-js'

/**
 * The single Stripe.js seam for the client. Publishable-key discovery, the
 * memoized Stripe.js loader, and the 3DS return URL all live here so the
 * showcase board and vitest can stub ONE module and never load the real
 * js.stripe.com script (see showcase/stubs/stripe.ts).
 */

/**
 * Publishable key from the layout meta tag (build-contract §3.7:
 * `fireside-stripe-publishable-key`, empty when payments are Mock/off).
 * null ⇒ payments aren't configured in this environment; the UI renders an
 * honest degraded state instead of a broken form.
 */
export function getStripePublishableKey(): string | null {
  const content = document
    .querySelector('meta[name="fireside-stripe-publishable-key"]')
    ?.getAttribute('content')
    ?.trim()
  return content ? content : null
}

let stripePromise: Promise<Stripe | null> | null = null

/**
 * Memoized Stripe.js load — the script is injected at most once per page.
 * Resolves null when no publishable key is present (degraded envs).
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const publishableKey = getStripePublishableKey()
    stripePromise = publishableKey ? loadStripe(publishableKey) : Promise.resolve(null)
  }
  return stripePromise
}

/**
 * Absolute return_url for confirmPayment — the /steam/wallet screen owns the
 * `?top_up_id&redirect_status` landing after a hosted 3DS redirect. (Return
 * URLs minted before the /steam move still land: /wallet 301s here with its
 * query preserved — see config/routes.rb.)
 */
export function walletReturnUrl(topUpId: string): string {
  return `${window.location.origin}/steam/wallet?top_up_id=${encodeURIComponent(topUpId)}`
}

/**
 * Absolute return_url for the club's confirmPayment — /join/welcome owns the
 * arrival, and syncs the subscription from this id. Only redirect-based
 * methods (a 3DS challenge) ever leave for it; a plain card confirms in place
 * and the screen navigates here itself.
 */
export function clubReturnUrl(subscriptionId: string): string {
  return `${window.location.origin}/join/welcome?subscription_id=${encodeURIComponent(subscriptionId)}`
}

/**
 * Absolute return_url for confirmSetup — /clubhouse/account owns the
 * `?setup_intent&redirect_status` landing after a 3DS redirect. Only
 * redirect-based methods use it; a plain card confirms in place.
 */
export function clubAccountReturnUrl(): string {
  return `${window.location.origin}/clubhouse/account`
}
